import {
  Brush,
  Clock,
  Download,
  Edit3,
  Eye,
  FileText,
  Image,
  Loader2,
  Paperclip,
  Reply,
  Trash2,
  Video,
  X,
} from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { useRef, useState, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Comment } from "@/types/types";
import { ConvexError } from "convex/values";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { formatDistance } from "date-fns";
import { formatTime } from "@/utils/format";
import { useConvexMutation } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

// Simple markdown to HTML converter
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return "";

  let html = markdown;

  // Convert bold (**text** or __text__) - must be done before italic
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");

  // Convert italic (*text* or _text_) - after bold to avoid conflicts
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  html = html.replace(/_(.+?)_/g, "<em>$1</em>");

  // Convert links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Convert line breaks
  html = html.replace(/\n/g, "<br>");

  return html;
};

// Extended comment type for comparison mode
type CommentWithVersion = Comment & {
  _versionInfo?: {
    versionNumber: number;
    versionId: string;
  };
};

interface CommentSidebarProps {
  comments: CommentWithVersion[];
  appFounders: Id<"users">[];
  onReply: (
    commentId: Id<"creatorVideoComments">,
    content: string,
    attachments?: Array<{
      storageId: Id<"_storage">;
      name: string;
      type: string;
      size: number;
    }>,
  ) => void;
  onSeekToComment: (time: number) => void;
  onDeleteComment: (commentId: Id<"creatorVideoComments">) => Promise<void>;
  onEditComment: (
    commentId: Id<"creatorVideoComments">,
    content: string,
  ) => Promise<void>;
  currentUser: Doc<"users"> | undefined;
  isCompareMode?: boolean;
  isLoading?: boolean;
  onDrawingDisplay?: (drawing: string | null) => void;
}

// Move RenderReplies component outside to prevent recreation on re-renders
const RenderReplies = ({
  replies,
  depth = 0,
  appFounders,
  currentUser,
  replyContent,
  setReplyContent,
  showReplyForm,
  setShowReplyForm,
  editContent,
  setEditContent,
  showEditForm,
  setShowEditForm,
  replyAttachments,
  setReplyAttachments,
  isUploadingReply,
  replyFileInputRefs,
  handleReply,
  handleReplyFileSelect,
  removeReplyAttachment,
  handleEdit,
  handleDelete,
  startEdit,
  getFileIcon,
  formatFileSize,
  canSubmitReply,
}: {
  replies: CommentWithVersion[];
  depth?: number;
  appFounders: Id<"users">[];
  currentUser: Doc<"users"> | undefined;
  replyContent: { [key: string]: string };
  setReplyContent: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  showReplyForm: { [key: string]: boolean };
  setShowReplyForm: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  editContent: { [key: string]: string };
  setEditContent: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  showEditForm: { [key: string]: boolean };
  setShowEditForm: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  replyAttachments: {
    [key: string]: Array<{
      file: File;
      storageId?: Id<"_storage">;
      uploading?: boolean;
    }>;
  };
  setReplyAttachments: React.Dispatch<
    React.SetStateAction<{
      [key: string]: Array<{
        file: File;
        storageId?: Id<"_storage">;
        uploading?: boolean;
      }>;
    }>
  >;
  isUploadingReply: { [key: string]: boolean };
  replyFileInputRefs: React.MutableRefObject<{
    [key: string]: HTMLInputElement | null;
  }>;
  handleReply: (commentId: Id<"creatorVideoComments">) => void;
  handleReplyFileSelect: (commentId: string, files: FileList | null) => void;
  removeReplyAttachment: (commentId: string, index: number) => void;
  handleEdit: (commentId: Id<"creatorVideoComments">) => void;
  handleDelete: (commentId: Id<"creatorVideoComments">) => Promise<void>;
  startEdit: (
    commentId: Id<"creatorVideoComments">,
    currentMessage: string,
  ) => void;
  getFileIcon: (type: string) => JSX.Element;
  formatFileSize: (bytes: number) => string;
  canSubmitReply: (commentId: string) => boolean;
}) => {
  // Filter out deleted replies
  const visibleReplies = replies.filter(
    (reply) => reply.message !== "This comment has been deleted",
  );

  return (
    <div
      className={`mt-3 space-y-2 min-w-0${
        depth === 0
          ? "pl-4 border-l border-border"
          : "pl-2 ml-2 border-l border-border/50"
      }`}
    >
      {visibleReplies.map((reply) => (
        <div key={reply._id} className="space-y-2 min-w-0">
          <div className="flex gap-2 min-w-0">
            <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center text-xs">
              {(reply.author as Doc<"users">).name?.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">
                    {(reply.author as Doc<"users">).name?.split(" ")[0]}
                  </span>
                  <Badge
                    variant={
                      TEAM_USER_IDS.includes(
                        (reply.author as Doc<"users">)._id,
                      ) ||
                      appFounders.includes((reply.author as Doc<"users">)._id)
                        ? "default"
                        : "secondary"
                    }
                    className="text-xs py-0"
                  >
                    {TEAM_USER_IDS.includes((reply.author as Doc<"users">)._id)
                      ? "Team"
                      : (reply.author as Doc<"users">).email ===
                          "system@creatorgrid.io"
                        ? "System"
                        : appFounders.includes(
                              (reply.author as Doc<"users">)._id,
                            )
                          ? "Brand"
                          : "Creator"}
                  </Badge>
                </div>
              </div>

              {showEditForm[reply._id] ? (
                <div className="space-y-2">
                  <Textarea
                    value={editContent[reply._id] || ""}
                    onChange={(e) =>
                      setEditContent((prev) => ({
                        ...prev,
                        [reply._id]: e.target.value,
                      }))
                    }
                    className="min-h-[50px] text-xs"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleEdit(reply._id)}
                      className="h-6 text-xs"
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setShowEditForm((prev) => ({
                          ...prev,
                          [reply._id]: false,
                        }))
                      }
                      className="h-6 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="text-xs text-primary mb-1 rich-text-display"
                    dangerouslySetInnerHTML={{
                      __html: markdownToHtml(reply.message),
                    }}
                  />

                  {/* Display attachments for replies */}
                  {reply.attachments && reply.attachments.length > 0 && (
                    <div className="space-y-1 mb-2">
                      {reply.attachments.map((attachment, index) => (
                        <AttachmentPreview
                          key={index}
                          attachment={attachment}
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    {(reply.author as Doc<"users">).email !==
                      "system@creatorgrid.io" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setShowReplyForm((prev) => ({
                            ...prev,
                            [reply._id]: !prev[reply._id],
                          }))
                        }
                        className="h-5 px-2 text-xs"
                      >
                        <Reply className="h-2 w-2 mr-1" />
                        Reply
                      </Button>
                    )}
                    {currentUser &&
                      (reply.author as Doc<"users">)._id ===
                        currentUser._id && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(reply._id, reply.message)}
                            className="h-5 px-2 text-xs"
                          >
                            <Edit3 className="h-2 w-2 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(reply._id)}
                            className="h-5 px-2 text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-2 w-2 mr-1" />
                            Delete
                          </Button>
                        </>
                      )}
                    <span className="text-xs text-primary/60 ml-auto">
                      {formatDistance(reply._creationTime, new Date(), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {showReplyForm[reply._id] && (
            <div className="ml-8 space-y-2">
              <Textarea
                placeholder="Write a reply..."
                value={replyContent[reply._id] || ""}
                onChange={(e) =>
                  setReplyContent((prev) => ({
                    ...prev,
                    [reply._id]: e.target.value,
                  }))
                }
                className="min-h-[50px] text-xs"
              />

              {/* Reply attachments preview */}
              {replyAttachments[reply._id] &&
                replyAttachments[reply._id].length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-primary/60">
                      Attachments
                    </div>
                    {replyAttachments[reply._id].map((attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border"
                      >
                        {getFileIcon(attachment.file.type)}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">
                            {attachment.file.name}
                          </div>
                          <div className="text-xs text-primary/60">
                            {formatFileSize(attachment.file.size)}
                          </div>
                        </div>
                        {attachment.uploading ? (
                          <div className="flex items-center gap-1 text-xs text-primary/60">
                            <Loader2 className="h-2 w-2 animate-spin" />
                            Uploading...
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              removeReplyAttachment(reply._id, index)
                            }
                            className="text-gray-400 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const input = replyFileInputRefs.current[reply._id];
                    if (input) input.click();
                  }}
                  disabled={isUploadingReply[reply._id]}
                  className="h-6 text-xs"
                >
                  <Paperclip className="h-2 w-2 mr-1" />
                  Attach
                </Button>
                <input
                  ref={(el) => {
                    replyFileInputRefs.current[reply._id] = el;
                  }}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                  className="hidden"
                  onChange={(e) =>
                    handleReplyFileSelect(reply._id, e.target.files)
                  }
                />
                <Button
                  size="sm"
                  onClick={() => handleReply(reply._id)}
                  disabled={!canSubmitReply(reply._id)}
                  className="h-6 text-xs"
                >
                  Reply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowReplyForm((prev) => ({
                      ...prev,
                      [reply._id]: false,
                    }));
                    // Clear reply data when canceling
                    setReplyContent((prev) => ({ ...prev, [reply._id]: "" }));
                    setReplyAttachments((prev) => ({
                      ...prev,
                      [reply._id]: [],
                    }));
                  }}
                  className="h-6 text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Recursively render nested replies */}
          {reply.replies && reply.replies.length > 0 && (
            <RenderReplies
              replies={reply.replies}
              depth={depth + 1}
              appFounders={appFounders}
              currentUser={currentUser}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              showReplyForm={showReplyForm}
              setShowReplyForm={setShowReplyForm}
              editContent={editContent}
              setEditContent={setEditContent}
              showEditForm={showEditForm}
              setShowEditForm={setShowEditForm}
              replyAttachments={replyAttachments}
              setReplyAttachments={setReplyAttachments}
              isUploadingReply={isUploadingReply}
              replyFileInputRefs={replyFileInputRefs}
              handleReply={handleReply}
              handleReplyFileSelect={handleReplyFileSelect}
              removeReplyAttachment={removeReplyAttachment}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              startEdit={startEdit}
              getFileIcon={getFileIcon}
              formatFileSize={formatFileSize}
              canSubmitReply={canSubmitReply}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export const CommentSidebar = ({
  comments,
  appFounders,
  onReply,
  onSeekToComment,
  onDeleteComment,
  onEditComment,
  currentUser,
  isCompareMode = false,
  isLoading = false,
}: CommentSidebarProps) => {
  // Filter comments based on user role and teamVideoReviewRequired
  const filteredComments = useMemo(() => {
    if (!comments || !currentUser) return comments;

    const isTeamMember = TEAM_USER_IDS.includes(currentUser._id);

    // Team members always see everything
    if (isTeamMember) {
      return comments;
    }

    // For brand accounts, check if teamVideoReviewRequired is enabled
    // This filtering is now handled on the backend, so we just return the comments
    return comments;
  }, [comments, currentUser]);
  const { toast } = useToast();
  const [replyContent, setReplyContent] = useState<{ [key: string]: string }>(
    {},
  );
  const [showReplyForm, setShowReplyForm] = useState<{
    [key: string]: boolean;
  }>({});
  const [editContent, setEditContent] = useState<{ [key: string]: string }>({});
  const [showEditForm, setShowEditForm] = useState<{
    [key: string]: boolean;
  }>({});

  // Add reply attachment states
  const [replyAttachments, setReplyAttachments] = useState<{
    [key: string]: Array<{
      file: File;
      storageId?: Id<"_storage">;
      uploading?: boolean;
    }>;
  }>({});
  const [isUploadingReply, setIsUploadingReply] = useState<{
    [key: string]: boolean;
  }>({});
  const replyFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
    {},
  );

  // Add ref to track which comment is currently uploading
  const currentUploadingComment = useRef<string | null>(null);

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      const uploadedFiles = uploaded.map((upload) => ({
        storageId: (upload.response as { storageId: Id<"_storage"> }).storageId,
        name: upload.name,
        type: upload.type || "application/octet-stream",
        size: 0, // We'll get the size from the original file in our state
      }));

      // Only update the specific comment that triggered this upload
      const commentId = currentUploadingComment.current;
      if (commentId) {
        setReplyAttachments((prev) => {
          const currentAttachments = prev[commentId] || [];

          // Create updated attachments array
          let uploadIndex = 0;
          const updatedAttachments = currentAttachments.map((att) => {
            if (att.uploading && uploadIndex < uploadedFiles.length) {
              const result = {
                ...att,
                storageId: uploadedFiles[uploadIndex].storageId,
                uploading: false,
              };
              uploadIndex++;
              return result;
            }
            return att;
          });

          return {
            ...prev,
            [commentId]: updatedAttachments,
          };
        });

        setIsUploadingReply((prev) => ({ ...prev, [commentId]: false }));
      }

      // Clear the current uploading comment reference
      currentUploadingComment.current = null;
    },
    onUploadError: () => {
      toast({
        title: "Error uploading files",
        description: "There was an error uploading your files",
        variant: "destructive",
      });

      // Reset uploading states for the specific comment
      const commentId = currentUploadingComment.current;
      if (commentId) {
        setIsUploadingReply((prev) => ({ ...prev, [commentId]: false }));
        setReplyAttachments((prev) => ({
          ...prev,
          [commentId]:
            prev[commentId]?.map((att) => ({ ...att, uploading: false })) || [],
        }));
      }

      // Clear the current uploading comment reference
      currentUploadingComment.current = null;
    },
  });

  const handleReply = (commentId: Id<"creatorVideoComments">) => {
    if (
      replyContent[commentId]?.trim() ||
      replyAttachments[commentId]?.length > 0
    ) {
      const finalAttachments = replyAttachments[commentId]
        ?.filter((att) => att.storageId)
        ?.map((att) => ({
          storageId: att.storageId as Id<"_storage">,
          name: att.file.name,
          type: att.file.type,
          size: att.file.size,
        }));

      onReply(
        commentId,
        replyContent[commentId] || "",
        finalAttachments?.length > 0 ? finalAttachments : undefined,
      );
      setReplyContent((prev) => ({ ...prev, [commentId]: "" }));
      setReplyAttachments((prev) => ({ ...prev, [commentId]: [] }));
      setShowReplyForm((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  const handleReplyFileSelect = (commentId: string, files: FileList | null) => {
    if (!files) return;

    // Set the current uploading comment reference
    currentUploadingComment.current = commentId;

    const newFiles = Array.from(files).map((file) => ({
      file,
      uploading: true,
    }));

    setReplyAttachments((prev) => ({
      ...prev,
      [commentId]: [...(prev[commentId] || []), ...newFiles],
    }));
    setIsUploadingReply((prev) => ({ ...prev, [commentId]: true }));
    startUpload(Array.from(files));
  };

  const removeReplyAttachment = (commentId: string, index: number) => {
    setReplyAttachments((prev) => ({
      ...prev,
      [commentId]: prev[commentId]?.filter((_, i) => i !== index) || [],
    }));
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return <Image className="h-3 w-3" />;
    if (type.startsWith("video/")) return <Video className="h-3 w-3" />;
    return <FileText className="h-3 w-3" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const canSubmitReply = (commentId: string) => {
    const hasContent = replyContent[commentId]?.trim();
    const hasAttachments = replyAttachments[commentId]?.length > 0;
    const isUploading = isUploadingReply[commentId];
    const hasUploadingFiles = replyAttachments[commentId]?.some(
      (att) => att.uploading,
    );

    return (
      Boolean(hasContent || hasAttachments) &&
      !isUploading &&
      !hasUploadingFiles
    );
  };

  const handleEdit = (commentId: Id<"creatorVideoComments">) => {
    if (editContent[commentId]?.trim()) {
      onEditComment(commentId, editContent[commentId]);
      setEditContent((prev) => ({ ...prev, [commentId]: "" }));
      setShowEditForm((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  const handleDelete = async (commentId: Id<"creatorVideoComments">) => {
    try {
      await onDeleteComment(commentId);
      toast({
        title: "Comment deleted",
        description: "Your comment has been deleted",
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: "Error deleting comment",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error deleting your comment",
        variant: "destructive",
      });
    }
  };

  const startEdit = (
    commentId: Id<"creatorVideoComments">,
    currentMessage: string,
  ) => {
    setEditContent((prev) => ({ ...prev, [commentId]: currentMessage }));
    setShowEditForm((prev) => ({ ...prev, [commentId]: true }));
  };

  const sortedComments = [...filteredComments].sort(
    (a, b) => b._creationTime - a._creationTime,
  );

  // Filter out deleted comments
  const visibleComments = sortedComments.filter(
    (comment) => comment.message !== "This comment has been deleted",
  );

  return (
    <div className="flex flex-col h-full" style={{ minWidth: "350px" }}>
      <h3 className="font-medium text-primary mb-4 flex-shrink-0">
        Comments ({visibleComments.length})
      </h3>

      <div className="flex-1 overflow-auto space-y-4 pr-2 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] min-w-0">
        {isLoading ? (
          <Card className="p-6 text-center">
            <div className="text-primary/60">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
              <p className="text-sm">Loading comments...</p>
            </div>
          </Card>
        ) : visibleComments.length === 0 ? (
          <Card className="p-6 text-center">
            <div className="text-primary/60">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary flex items-center justify-center">
                <Reply className="h-6 w-6" />
              </div>
              <p className="text-sm">
                Comments will appear here once they are created
              </p>
              <p className="text-xs mt-1">
                Click on the message icon inside the video player to add your
                first comment
              </p>
            </div>
          </Card>
        ) : (
          visibleComments.map((comment) => (
            <Card key={comment._id} className="p-4 min-w-0 overflow-x-auto">
              <div className="flex gap-3 min-w-0">
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium">
                  {(comment.author as Doc<"users">).name?.charAt(0)}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {(comment.author as Doc<"users">).name?.split(" ")[0]}
                      </span>
                      <Badge
                        variant={
                          TEAM_USER_IDS.includes(
                            (comment.author as Doc<"users">)._id,
                          ) ||
                          appFounders.includes(
                            (comment.author as Doc<"users">)._id,
                          )
                            ? "default"
                            : "secondary"
                        }
                      >
                        {TEAM_USER_IDS.includes(
                          (comment.author as Doc<"users">)._id,
                        )
                          ? "Team"
                          : (comment.author as Doc<"users">).email ===
                              "system@creatorgrid.io"
                            ? "System"
                            : appFounders.includes(
                                  (comment.author as Doc<"users">)._id,
                                )
                              ? "Brand"
                              : "Creator"}
                      </Badge>
                      {/* Show version badge in compare mode */}
                      {isCompareMode && comment._versionInfo && (
                        <Badge variant="outline" className="text-xs">
                          v{comment._versionInfo.versionNumber}
                        </Badge>
                      )}
                      {/* Drawing indicator */}
                      {comment.drawing && (
                        <Badge
                          variant="outline"
                          className="text-xs flex items-center gap-1"
                        >
                          <Brush className="h-3 w-3" />
                          Drawing
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          onSeekToComment(comment.timestampRange.start)
                        }
                        className="h-6 px-2 text-xs"
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        {formatTime(comment.timestampRange.start)}
                        {comment.timestampRange.end &&
                          ` - ${formatTime(comment.timestampRange.end)}`}
                      </Button>
                    </div>
                  </div>

                  {showEditForm[comment._id] ? (
                    <div className="space-y-2 mb-2">
                      <Textarea
                        value={editContent[comment._id] || ""}
                        onChange={(e) =>
                          setEditContent((prev) => ({
                            ...prev,
                            [comment._id]: e.target.value,
                          }))
                        }
                        className="min-h-[60px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleEdit(comment._id)}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setShowEditForm((prev) => ({
                              ...prev,
                              [comment._id]: false,
                            }))
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="text-sm text-primary mb-2 rich-text-display"
                      dangerouslySetInnerHTML={{
                        __html: markdownToHtml(comment.message),
                      }}
                    />
                  )}

                  {/* Display attachments */}
                  {comment.attachments && comment.attachments.length > 0 && (
                    <div className="space-y-2 mb-2">
                      {comment.attachments.map((attachment, index) => (
                        <AttachmentPreview
                          key={index}
                          attachment={attachment}
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    {(comment.author as Doc<"users">).email !==
                      "system@creatorgrid.io" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setShowReplyForm((prev) => ({
                            ...prev,
                            [comment._id]: !prev[comment._id],
                          }))
                        }
                        className="h-6 px-2 text-xs"
                      >
                        <Reply className="h-3 w-3 mr-1" />
                        Reply
                      </Button>
                    )}
                    {currentUser &&
                      (comment.author as Doc<"users">)._id ===
                        currentUser._id && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              startEdit(comment._id, comment.message)
                            }
                            className="h-6 px-2 text-xs"
                          >
                            <Edit3 className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(comment._id)}
                            className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </>
                      )}
                    <span className="text-xs text-primary/60 ml-auto">
                      {formatDistance(comment._creationTime, new Date(), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {showReplyForm[comment._id] && (
                <div className="mt-2 space-y-2">
                  <Textarea
                    placeholder="Write a reply..."
                    value={replyContent[comment._id] || ""}
                    onChange={(e) =>
                      setReplyContent((prev) => ({
                        ...prev,
                        [comment._id]: e.target.value,
                      }))
                    }
                    className="min-h-[60px]"
                  />

                  {/* Main comment reply attachments preview */}
                  {replyAttachments[comment._id] &&
                    replyAttachments[comment._id].length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-primary/60">
                          Attachments
                        </div>
                        {replyAttachments[comment._id].map(
                          (attachment, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border"
                            >
                              {getFileIcon(attachment.file.type)}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">
                                  {attachment.file.name}
                                </div>
                                <div className="text-xs text-primary/60">
                                  {formatFileSize(attachment.file.size)}
                                </div>
                              </div>
                              {attachment.uploading ? (
                                <div className="flex items-center gap-1 text-xs text-primary/60">
                                  <Loader2 className="h-2 w-2 animate-spin" />
                                  Uploading...
                                </div>
                              ) : (
                                <button
                                  onClick={() =>
                                    removeReplyAttachment(comment._id, index)
                                  }
                                  className="text-gray-400 hover:text-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    )}

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const input = replyFileInputRefs.current[comment._id];
                        if (input) input.click();
                      }}
                      disabled={isUploadingReply[comment._id]}
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      Attach
                    </Button>
                    <input
                      ref={(el) => {
                        replyFileInputRefs.current[comment._id] = el;
                      }}
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                      className="hidden"
                      onChange={(e) =>
                        handleReplyFileSelect(comment._id, e.target.files)
                      }
                    />
                    <Button
                      size="sm"
                      onClick={() => handleReply(comment._id)}
                      disabled={!canSubmitReply(comment._id)}
                    >
                      Reply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowReplyForm((prev) => ({
                          ...prev,
                          [comment._id]: false,
                        }));
                        // Clear reply data when canceling
                        setReplyContent((prev) => ({
                          ...prev,
                          [comment._id]: "",
                        }));
                        setReplyAttachments((prev) => ({
                          ...prev,
                          [comment._id]: [],
                        }));
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {comment.replies && comment.replies.length > 0 && (
                <RenderReplies
                  replies={comment.replies}
                  appFounders={appFounders}
                  currentUser={currentUser}
                  replyContent={replyContent}
                  setReplyContent={setReplyContent}
                  showReplyForm={showReplyForm}
                  setShowReplyForm={setShowReplyForm}
                  editContent={editContent}
                  setEditContent={setEditContent}
                  showEditForm={showEditForm}
                  setShowEditForm={setShowEditForm}
                  replyAttachments={replyAttachments}
                  setReplyAttachments={setReplyAttachments}
                  isUploadingReply={isUploadingReply}
                  replyFileInputRefs={replyFileInputRefs}
                  handleReply={handleReply}
                  handleReplyFileSelect={handleReplyFileSelect}
                  removeReplyAttachment={removeReplyAttachment}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  startEdit={startEdit}
                  getFileIcon={getFileIcon}
                  formatFileSize={formatFileSize}
                  canSubmitReply={canSubmitReply}
                />
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Add AttachmentPreview component
const AttachmentPreview = ({
  attachment,
}: {
  attachment: {
    storageId: Id<"_storage">;
    name: string;
    type: string;
    size: number;
  };
}) => {
  const { data: attachmentUrl } = useQuery(
    convexQuery(api.creator_internal_videos.getAttachmentUrl, {
      storageId: attachment.storageId,
    }),
  );

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return <Image className="h-4 w-4" />;
    if (type.startsWith("video/")) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isImage = attachment.type.startsWith("image/");
  const isVideo = attachment.type.startsWith("video/");

  return (
    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border mt-2">
      {getFileIcon(attachment.type)}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{attachment.name}</div>
        <div className="text-xs text-primary/60">
          {formatFileSize(attachment.size)}
        </div>
      </div>
      <div className="flex gap-1">
        {(isImage || isVideo) && attachmentUrl && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => window.open(attachmentUrl, "_blank")}
            className="h-6 px-2"
          >
            <Eye className="h-3 w-3" />
          </Button>
        )}
        {attachmentUrl && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const a = document.createElement("a");
              a.href = attachmentUrl;
              a.download = attachment.name;
              a.click();
            }}
            className="h-6 px-2"
          >
            <Download className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};
